import { useState } from "react"
import { Dialog, DialogContent, Box, Typography, IconButton, Button, ArrowBackIcon, CloseIcon, Loader } from 'src/components';
import { useIsMobile, useSnackbar } from 'src/hooks';
import { membershipsData } from 'src/data';
import { useAuth, createStripeCheckoutSession } from 'src/utils';

import PlanSwitch from './components/plan-switch';
import FeatureList from './components/feature-list';
import BillingIntervalOptions from './components/billing-interval-options';

import backgroundImage from 'src/assets/alaska.jpg';

export default function MembershipsModal({ open, onClose }) {
    const [selectedTier, setSelectedTier] = useState("Plus");
    const [billingCycle, setBillingCycle] = useState("Yearly");
    const [loadingStripeCheckout, setLoadingStripeCheckout] = useState(false);

    const { user } = useAuth();
    const { openSnackbar } = useSnackbar();
    const isMobile = useIsMobile();

    const currentTier = membershipsData[selectedTier];

    const billingOptions = [
        { type: "Monthly", price: `£${currentTier.monthlyPrice}/mo`, subText: `Only £${currentTier.dailyPrice} per day`, highlight: false },
        { type: "Yearly", price: `£${currentTier.yearlyPrice}/mo`, subText: "Less than 1 coffee a month", highlight: true, savePercent: currentTier.savePercent },
    ];

    const handleCheckout = async () => {
        setLoadingStripeCheckout(true);
        try {
            const { url } = await createStripeCheckoutSession(
                `${selectedTier}${billingCycle}`,
                user.id,
                user.email,
                user.stripeCustomerId
            );
            window.open(url, "_blank"); // redirect to Stripe
        } catch (err) {
            console.error('Failed to start checkout', err);
            openSnackbar("Please try again later", "error");
        } finally {
            setLoadingStripeCheckout(false); 
        }
    }

    return (
        <Dialog
        open={open}
        onClose={onClose}
        fullScreen={isMobile}
        maxWidth="xs"
        fullWidth
        PaperProps={{
            sx: {
                borderRadius: isMobile ? 0 : 2,
                overflow: "hidden"
            },
        }}
        >
        <DialogContent className="scrollBox" sx={{ p: 0, position: "relative" }}>
            
            {/* Close Button */}
            <IconButton
                onClick={onClose}
                size="small"
                sx={{
                    position: "absolute",
                    top: isMobile ? 'calc(12px + var(--safe-area-top))' : 12,
                    left: isMobile ? 12 : "auto",
                    right: isMobile ? "auto" : 12,
                    zIndex: 1,
                    backgroundColor: "rgba(255,255,255,0.8)",
                    "&:hover": { backgroundColor: "rgba(255,255,255,1)" },
                }}
                >
                {isMobile ? <ArrowBackIcon /> : <CloseIcon />}
            </IconButton>

            {/* Hero Image */}
            <Box
                sx={{
                    height: "35vh",
                    backgroundImage: `url(${backgroundImage})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                    width: "100%",
                }}
            />

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    p: 3,
                    mt: -5,
                    bgcolor: "#f2f5f7",
                    borderTopRightRadius: 30,
                    borderTopLeftRadius: 30
                }}
                >
                {/* Title */}
                <Typography
                    component="h1"
                    sx={{
                        fontSize: "23px",
                        fontWeight: "bold",
                        textAlign: "center",
                        mb: 3,
                        color: "#1a1a1a",
                    }}
                >
                    Enhance your adventures
                </Typography>

                <PlanSwitch selectedTier={selectedTier} onSelect={setSelectedTier} />

                {/* Features Box */}
                <Box
                    sx={{
                        bgcolor: "white",
                        borderRadius: 3,
                        px: 3,
                        pb: 3,
                        pt: 1,
                        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    }}
                >
                    <FeatureList features={currentTier.features} />

                    <BillingIntervalOptions
                        options={billingOptions}
                        selected={billingCycle}
                        onSelect={setBillingCycle}
                    />

                </Box>
            </Box>

            {/* Sticky Subscribe Button */}
            <Box
                sx={{
                    position: "sticky",
                    bottom: 0,
                    bgcolor: "white",
                    p: 2,
                    borderTop: "1px solid #e0e0e0",
                    zIndex: 10,
                }}
                >
                <Button
                    variant="contained"
                    onClick={handleCheckout}
                    fullWidth
                    size="large"
                    sx={{
                        py: 1.5,
                        borderRadius: 50,
                        fontWeight: "bold",
                        fontSize: "16px"
                    }}
                >
                    Subscribe now!
                </Button>
            </Box>
        </DialogContent>

        {loadingStripeCheckout && <Loader /> }

        </Dialog>
    )
}
